/* eslint-disable import/no-webpack-loader-syntax, @typescript-eslint/member-ordering */
import { last } from 'lodash';
import dts from '!!raw-loader!./wuji-flow/index.d.ts';
import { workflowEntityApi, flowApi } from '@/services';
import { addPathPrefix } from '@utils/path';
import wujiFetch from '@utils/wujiFetch';
import { FLOW_JOB_STATUS_META } from './wuji-flow/statusMeta';
export { FLOW_JOB_STATUS_META };
const buildAdminJobListQuery = (query, pageInfo) => {
    const filterParts = [
        query?.flowId ? `flowId="${query.flowId}"` : undefined,
        pageInfo?.filter,
    ].filter((part) => Boolean(part));
    return {
        title: query?.title,
        creator: query?.creator,
        flowVersionId: query?.flowVersionId,
        jobStatus: query?.jobStatus,
        currentApprovers: query?.currentApprovers,
        relatedUsers: query?.relatedUsers,
        ctime__gt: query?.ctime__gt,
        ctime__lt: query?.ctime__lt,
        filter: filterParts.length > 0 ? filterParts.join('&') : undefined,
        page: pageInfo?.page,
        size: pageInfo?.size,
    };
};
const buildFlowWebhookPath = (webhookKey, jobId, projectId) => {
    if (!webhookKey)
        throw new Error('webhookKey is required');
    if (!jobId)
        throw new Error('jobId is required');
    if (!projectId)
        throw new Error('projectId is required');
    const query = new URLSearchParams({ jobid: jobId, projectid: projectId });
    return `/api/xy/public/flow/webhook/${encodeURIComponent(webhookKey)}?${query.toString()}`;
};
export async function getTypeDefinitions() {
    return dts;
}
export default class WujiFlow {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    FLOW_JOB_STATUS_META = FLOW_JOB_STATUS_META;
    startBasicFlow = async (id, params, extraPayload) => await workflowEntityApi.start(window.w.projectId, window.w.env, id, { ...params, _wuji_extra_data: extraPayload });
    getBasicFlowService = async (entityId) => {
        const { projectId, env } = window.w;
        const entity = await workflowEntityApi.fetchWorkflowEntity(projectId, env, entityId);
        return new BasicFlowService(entity);
    };
    fetchMyEntityList = async (pageInfo) => {
        const { projectId, env } = window.w;
        return await workflowEntityApi.fetchEntities(projectId, env, 'created', pageInfo);
    };
    fetchMyEntityListTotal = async (pageInfo) => {
        const { projectId, env } = window.w;
        const resp = await workflowEntityApi.fetchEntities(projectId, env, 'created', { ...pageInfo, count: 1 });
        return resp.total;
    };
    fetchPendingEntityList = async (pageInfo) => {
        const { projectId, env } = window.w;
        return await workflowEntityApi.fetchEntities(projectId, env, 'pending', pageInfo);
    };
    fetchPendingEntityListTotal = async (pageInfo) => {
        const { projectId, env } = window.w;
        const resp = await workflowEntityApi.fetchEntities(projectId, env, 'pending', { ...pageInfo, count: 1 });
        return resp.total;
    };
    fetchRelatedEntityList = async (pageInfo) => {
        const { projectId, env } = window.w;
        return await workflowEntityApi.fetchEntities(projectId, env, 'related', pageInfo);
    };
    fetchRelatedEntityListTotal = async (pageInfo) => {
        const { projectId, env } = window.w;
        const resp = await workflowEntityApi.fetchEntities(projectId, env, 'related', { ...pageInfo, count: 1 });
        return resp.total;
    };
    startAdvancedFlow = async (id, params) => (await flowApi.startFlow(window.w.projectId, id, params));
    getAdvancedFlowService = async (jobId) => {
        const { projectId } = window.w;
        const job = await flowApi.fetchJob(projectId, '', jobId);
        return new AdvancedFlowService(job);
    };
    getFlowWebhookUrl = (webhookKey, jobId) => {
        const path = buildFlowWebhookPath(webhookKey, jobId, window.w.projectId);
        return `${window.location.origin}${addPathPrefix(path)}`;
    };
    callFlowWebhook = async (webhookKey, jobId, data) => await wujiFetch(buildFlowWebhookPath(webhookKey, jobId, window.w.projectId), {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(data ?? {}),
    });
    fetchMyJobList = async (title, pageInfo) => {
        const { projectId } = window.w;
        return await flowApi.fetchJobListByType(projectId, title, 'created', pageInfo);
    };
    fetchMyJobListTotal = async (title, pageInfo) => {
        const { projectId } = window.w;
        const resp = await flowApi.fetchJobListByType(projectId, title, 'created', { ...pageInfo, count: 1 });
        return resp.total;
    };
    fetchPendingJobList = async (title, pageInfo) => {
        const { projectId } = window.w;
        return await flowApi.fetchJobListByType(projectId, title, 'pending', pageInfo);
    };
    fetchPendingJobListTotal = async (title, pageInfo) => {
        const { projectId } = window.w;
        const resp = await flowApi.fetchJobListByType(projectId, title, 'pending', { ...pageInfo, count: 1 });
        return resp.total;
    };
    fetchRelatedJobList = async (title, pageInfo) => {
        const { projectId } = window.w;
        return await flowApi.fetchJobListByType(projectId, title, 'participated', pageInfo);
    };
    fetchRelatedJobListTotal = async (title, pageInfo) => {
        const { projectId } = window.w;
        const resp = await flowApi.fetchJobListByType(projectId, title, 'participated', { ...pageInfo, count: 1 });
        return resp.total;
    };
    /** 仅管理员可用。列出当前环境全部流程单，非管理员会 403。 */
    fetchJobList = async (query, pageInfo) => {
        const { projectId } = window.w;
        return await flowApi.fetchJobList(projectId, buildAdminJobListQuery(query, pageInfo));
    };
    /** 仅管理员可用。当前环境全部流程单条数，非管理员会 403。 */
    fetchJobListLength = async (query, pageInfo) => {
        const { projectId } = window.w;
        const resp = await flowApi.fetchJobList(projectId, {
            ...buildAdminJobListQuery(query, pageInfo),
            count: 1,
        });
        return resp.total;
    };
    get default() {
        return this;
    }
}
class BasicFlowService {
    data;
    constructor(data) {
        this.data = data;
        this.data.approvalPayload = JSON.parse(data.payload);
    }
    getEntityData = () => this.data;
    approve = async (data) => {
        const { projectId, env, _id: id } = this.data;
        this.data = await workflowEntityApi.transition(projectId, env, id, { ...data, currentStatus: this.data.status });
        return this.data;
    };
    restart = async (payload) => {
        const { projectId, env, _id: id } = this.data;
        this.data = await workflowEntityApi.restart(projectId, env, id, payload);
        return this.data;
    };
    abort = async (comment) => {
        const { projectId, env, _id: id } = this.data;
        this.data = await workflowEntityApi.abort(projectId, env, id, { comment });
        return this.data;
    };
    assignHandlers = async (handlers) => {
        const { projectId, env, _id: id } = this.data;
        this.data = await workflowEntityApi.assignHandlers(projectId, env, id, handlers);
        return this.data;
    };
    fetchRecordList = async () => {
        const { projectId, env, _id: id } = this.data;
        const records = await workflowEntityApi.fetchEntityRecords(projectId, env, id);
        return records;
    };
    isProcessing = () => this.data.flowStatus === 'processing';
    canApprove = () => {
        const { userId } = window.w.user;
        return this.isProcessing() && this.data.handlers.split(',').includes(userId);
    };
}
class AdvancedFlowService {
    data;
    constructor(data) {
        this.data = data;
        this.data = data;
    }
    getJobData = () => this.data;
    approve = async (data) => {
        const nodeId = data.nodeId ?? last(this.data.detailBlocks)?.nodeId;
        return await flowApi.approval(this.data.projectId, nodeId, this.data.id, data.operation, data.comment, data.form);
    };
    fetchApprovalNodeInfo = async (jobId, nodeId, tickId) => await flowApi.fetchApprovalNodeInfo(jobId, nodeId, tickId);
    fetchLogNodeInfo = async (jobId, nodeId, tickId) => await flowApi.fetchLogNodeInfo(jobId, nodeId, tickId);
    modifyApprover = async (nodeId, approvers) => (await flowApi.updateApprovers(this.data.projectId, this.data.id, nodeId, approvers));
    cancelApproval = async (nodeId, comment) => (await flowApi.cancelApproval(this.data.projectId, this.data.id, nodeId, comment));
    run = async () => {
        if (this.data.jobStatus === 'suspended') {
            throw new Error('Cannot run a suspended job; use approve, webhook, or cancelApproval');
        }
        await flowApi.runJob(this.data.projectId, this.data.id);
        this.data = await flowApi.fetchJob(this.data.projectId, '', this.data.id);
        return this.data;
    };
    pause = async () => {
        await flowApi.pauseJob(this.data.projectId, this.data.id);
        this.data = await flowApi.fetchJob(this.data.projectId, '', this.data.id);
        return this.data;
    };
    abort = async () => {
        await flowApi.abortJob(this.data.projectId, this.data.id);
        this.data = await flowApi.fetchJob(this.data.projectId, '', this.data.id);
        return this.data;
    };
}
